import {
  EDatasourceType,
  EditableSourceCodec,
  EStatementTypes,
  SpecialDIIds,
  type IActionDefinitionDto,
  type IDisplayItem,
  type IValidation
} from "@kuika/kuika-cl-model";
import { summarizeCondition } from "../../../../../shared/utility/designer-validation-input-actions";
import {
  findManagedTaskScreenLink,
  isManagedTaskScreenId,
  type IManagedTaskScreenLink
} from "../../../../../shared/utility/managed-task-screen";
import { simplifyStyleObj } from "../../../../../shared/utility/mcp-tools/helpers/style-helpers";
import {
  ScreenIndex,
  buildScreenIndex,
  collectNavigationTargets
} from "../../../../../shared/utility/navigation-target-collector";
import type { ContextPillData } from "../shared/mcp-chat-types";

const DASHBOARD_STATE_MAP: Record<number, string> = {
  1: "design",
  9: "datasources",
  11: "process-automation",
  12: "config-manager",
  13: "email-builder",
  14: "scheduled-jobs",
  15: "api-management",
  17: "report-designer"
};

const SCREEN_TYPE_LABELS: Record<number, string> = {
  1: "Default",
  2: "Report",
  3: "Master",
  4: "WebScreen",
  5: "Component"
};

const TREE_KEY_PROPS = new Set([
  "value",
  "label",
  "placeholder",
  "tab",
  "iconName",
  "count",
  "columns",
  "title",
  "text",
  "src",
  "checked"
]);

const editableSourceCodec = new EditableSourceCodec({ simplifyStyleObj });

// Legacy action definitions may carry an `actionUniqueName` alongside `name`.
// The field is not declared on IActionDefinitionDto, so we widen locally.
interface NamedActionDefinition {
  id?: string;
  name?: string;
  actionUniqueName?: string;
}

function getScreenRoles(screenId: string, project: any): string[] {
  if (!screenId || !project) return [];
  const roles: string[] = [];
  if (project.startingScreenId === screenId) roles.push("Starting Screen");
  if (project.signinScreenId === screenId) roles.push("Signin Screen");
  if (project.leftMenuScreenId === screenId) roles.push("Left Menu Screen");
  if (project.resetPasswordScreenId === screenId) roles.push("Reset Password Screen");
  if (project.splashScreenId === screenId) roles.push("Splash Screen");
  if (project.newPasswordScreenId === screenId) roles.push("New Password Screen");
  if (project.verificationCodeScreenId === screenId) roles.push("Verification Code Screen");
  if (project.offlineScreenId === screenId) roles.push("Offline Screen");
  if (project.documentUploadLinkScreenId === screenId) roles.push("Document Upload Link Screen");
  if (isManagedTaskScreenId(project, screenId)) {
    roles.push("Process Step Screen");
  }
  return roles;
}

export function mapDashboardState(state: number): string {
  return DASHBOARD_STATE_MAP[state] || `unknown(${state})`;
}

export function buildEditableScreenSource(
  screenDesign: any,
  screenDictionary?: Record<number, any>,
  project?: any
): string {
  return editableSourceCodec.serializeScreen({
    screenDesign,
    screenDictionary,
    componentDefinitions: project?.componentSetObj?.components
  });
}

export function buildLightTreeNode(di: any, depth: number, maxDepth: number, screenIndex?: ScreenIndex): any {
  const node: any = {
    kuikaId: di.id?.toString(),
    name: di.item?.name,
    friendlyName: di.friendlyName || undefined,
    contractId: di.contractId || undefined,
    dataRef: di.dataRef || undefined,
    fieldRef: di.fieldRef || undefined,
    interactionRef: di.interactionRef || undefined,
    childCount: di.children?.length || 0
  };

  const props: Record<string, string> = {};
  if (di.item?.properties) {
    for (const prop of di.item.properties) {
      if (!TREE_KEY_PROPS.has(prop.name)) continue;
      if (prop.selectedType === 3) continue;
      const val = prop.symbol?.fixedValue;
      if (val == null || val === "" || typeof val === "object") continue;
      props[prop.name] = String(val);
    }
  }

  // Surface static NAVIGATE targets so the LLM can correlate visible labels
  // with their destination screens (e.g. when renaming Sidebar Menu items).
  // Without this, the tree shows only friendlyName + props and the LLM has
  // no signal that "MenuItem A" navigates to "HomeScreen" — it picks names
  // semantically and the result mismatches the wiring.
  if (screenIndex && screenIndex.byId.size > 0) {
    const navTargets = collectNavigationTargets(di, screenIndex);
    if (navTargets) props.navigatesTo = navTargets;
  }

  if (Object.keys(props).length > 0) node.props = props;

  if (di.children && di.children.length > 0 && depth < maxDepth) {
    node.children = di.children.map((child: any) => buildLightTreeNode(child, depth + 1, maxDepth, screenIndex));
  }

  return node;
}

// ─── Datasource dependency graph ────────────────────────────────────────
// A pivoted view of the screen: for each datasource, which components bind
// to it as a read (property binding like `dataSource`) vs mutation (event
// action). The LLM uses this to decide whether a mutation action should
// be chained with the corresponding read to refresh dependent UI. See
// KOA-17026 for context.

export interface ComponentBinding {
  kuikaId: string;
  componentName: string;
  actionName: string;
  // "dataSource" for property bindings, event name (e.g. "onClick",
  // "onRowDelete") for event bindings.
  bindingPoint: string;
}

export interface ScreenDatasourceDependency {
  datasourceId: string;
  datasourceName: string;
  reads: ComponentBinding[];
  mutations: ComponentBinding[];
}

type BindingKind = "read" | "mutation";

function classifyStatement(statementType: EStatementTypes | undefined): BindingKind | null {
  if (statementType == null) return null;
  if (statementType === EStatementTypes.CustomExecuteReader || statementType === EStatementTypes.Select) {
    return "read";
  }
  if (
    statementType === EStatementTypes.CustomExecuteNonQuery ||
    statementType === EStatementTypes.Save ||
    statementType === EStatementTypes.Delete
  ) {
    return "mutation";
  }
  return null;
}

export function deriveDatasourceDependencies(
  displayItems: IDisplayItem[],
  actionDefinitions: IActionDefinitionDto[]
): ScreenDatasourceDependency[] {
  if (!displayItems.length || !actionDefinitions.length) return [];

  const actionById = new Map<string, IActionDefinitionDto>();
  for (const ad of actionDefinitions) {
    if (ad?.id) actionById.set(ad.id, ad);
  }

  const resultByDs = new Map<string, ScreenDatasourceDependency>();

  function record(actionDefId: string | undefined, kuikaId: string, componentName: string, bindingPoint: string): void {
    if (!actionDefId) return;
    const actionDef = actionById.get(actionDefId);
    if (!actionDef) return;
    const dsId = actionDef.kDatasource_Id;
    if (!dsId) return;

    const kind = classifyStatement(actionDef.statementType);
    if (!kind) return;

    let entry = resultByDs.get(dsId);
    if (!entry) {
      entry = {
        datasourceId: dsId,
        datasourceName: actionDef.kDatasource_Name || dsId,
        reads: [],
        mutations: []
      };
      resultByDs.set(dsId, entry);
    }

    const binding: ComponentBinding = { kuikaId, componentName, actionName: actionDef.name, bindingPoint };
    if (kind === "read") entry.reads.push(binding);
    else entry.mutations.push(binding);
  }

  function walk(di: IDisplayItem): void {
    if (!di) return;
    const kuikaId = di.id?.toString() || "";
    const item = di.item;
    if (item) {
      const componentName = item.name || "";

      if (item.properties) {
        for (const prop of item.properties) {
          const actionDefId = prop?.symbol?.actionDefinitionId;
          if (actionDefId) {
            record(actionDefId, kuikaId, componentName, prop.name || "property");
          }
        }
      }

      if (item.events) {
        for (const ev of item.events) {
          const actions = ev?.actions;
          if (!actions) continue;
          for (const action of actions) {
            if (action?.actionDefinitionId) {
              record(action.actionDefinitionId, kuikaId, componentName, ev.eventName || "event");
            }
          }
        }
      }
    }

    if (di.children) {
      for (const child of di.children) walk(child);
    }
  }

  for (const root of displayItems) walk(root);

  return Array.from(resultByDs.values()).filter((d) => d.reads.length > 0 || d.mutations.length > 0);
}

interface ContextValidationRule {
  message: string;
  condition?: string;
}

interface ContextValidationEntry {
  kuikaId: string;
  componentName: string;
  rules: ContextValidationRule[];
}

/**
 * Collect per-component input-validation rules across the whole screen so the
 * chat server can answer "list all validations" from context (no per-component
 * tool loop). Only components that actually carry rules are emitted.
 */
export function collectScreenValidations(
  screenDictionary: Record<number, IDisplayItem> | undefined
): ContextValidationEntry[] {
  if (!screenDictionary) return [];
  const entries: ContextValidationEntry[] = [];
  for (const key of Object.keys(screenDictionary)) {
    const di: IDisplayItem | undefined = screenDictionary[Number(key)];
    const validations: IValidation[] | undefined = di?.item?.validations;
    if (!Array.isArray(validations) || validations.length === 0) continue;
    const rules: ContextValidationRule[] = validations.map((v: IValidation): ContextValidationRule => {
      const condition: string | null = summarizeCondition(v.condition);
      return {
        message: v.message || "",
        ...(condition ? { condition } : {})
      };
    });
    entries.push({
      kuikaId: String(di.id),
      componentName: di.friendlyName || di.item?.name || "Component",
      rules
    });
  }
  return entries;
}

export function gatherScreenContext(): any | null {
  try {
    const sd = (window as any).getSdInstance?.();
    if (!sd) return null;

    const context: any = {};
    const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;
    const screenDesign = sd.screenProvider?.getScreenDesign?.();

    if (screenDesign) {
      context.screenName = screenDesign.name || sd.screenProvider?.state?.sdName;
      context.screenId = screenDesign.id || sd.screenProvider?.state?.sdId;

      const screenList = sd.screenProvider?.state?.screenList;
      if (screenList && context.screenId) {
        const currentScreen = screenList.find((s: any) => s.id === context.screenId);
        const screenTypeNum = currentScreen?.publicMetaData?.screenType;
        if (screenTypeNum != null) {
          context.screenType = SCREEN_TYPE_LABELS[screenTypeNum] || `Unknown(${screenTypeNum})`;
        }
      }
      // Project-screen list (KOA-16241): surface a compact {name, id} list so
      // the chat-server's classifier can disambiguate cross-screen edits
      // ("Signin ekranına bir buton ekle") against existing screens. The
      // orchestrator uses this both as a classifier hint and as the lookup
      // pool for the pre-pipeline cross-screen navigation guard. We emit it
      // unconditionally when the list is non-empty — even when the current
      // screen is the only one — because the chat-server filters out the
      // active screen before injecting it into the classifier prompt.
      if (Array.isArray(screenList) && screenList.length > 0) {
        const projectScreens = screenList
          .map((s: any) => ({
            name: typeof s?.name === "string" ? s.name : "",
            id: typeof s?.id === "string" ? s.id : ""
          }))
          .filter((s: { name: string; id: string }) => s.name.length > 0 && s.id.length > 0);
        if (projectScreens.length > 0) {
          context.projectScreens = projectScreens;
        }
      }

      if (project && context.screenId) {
        const roles = getScreenRoles(context.screenId, project);
        if (roles.length > 0) {
          context.screenRoles = roles;
        }
        // KOA-17362 §7.6 — the orchestrator needs this as a FLAG, not buried
        // in a human-readable role string it would have to parse. A managed
        // task screen's form is regenerated from workflow metadata, so
        // label / field-width / field-existence edits belong to
        // `ui_workflow_dsl`; the planner's pre-execution validator reads this
        // to route them there instead of hand-patching the tree (which the
        // backend would revert on the next `UpdateWorkflowScreens`).
        // Emitted unconditionally so `false` is distinguishable from absent.
        const managedLink: IManagedTaskScreenLink | null = findManagedTaskScreenLink(project, context.screenId);
        context.isProcessStepScreen = managedLink !== null;
        // …and the STEP IDENTITY with it. A redirect that names a tool but not
        // the workflow/step it applies to is a dead end: the planner has to
        // discover them, and `ui_workflow_dsl` cannot even run its discovery
        // `LIST` from a screen (no BPMN canvas is mounted → atomic abort). With
        // processId the plan can open the workflow itself; with stepId it can
        // address the step. Both are already in the link we just matched.
        if (managedLink && (managedLink.processId || managedLink.stepId)) {
          context.processStepScreen = managedLink;
        }
      }
    }

    const dashboardState = sd.designSituationProvider?.state?.dashboardState ?? sd.state?.dashboardState ?? null;
    if (dashboardState != null) {
      context.currentView = mapDashboardState(dashboardState);
    }

    const selectedDi = sd.displayItemProvider?.getSelectedDisplayItem?.();
    if (selectedDi) {
      context.selectedComponent = {
        kuikaId: selectedDi.id?.toString(),
        name: selectedDi.item?.name,
        friendlyName: selectedDi.friendlyName || undefined
      };
    }

    let allRoots: IDisplayItem[] = [];
    if (screenDesign) {
      const screenDictionary =
        sd.screenProvider?.getScreenDesignDictionary?.() || sd.screenProvider?.state?.screenDesignDictionary;

      // Mirror EditableSourceCodec.serializeScreen so treeSummary stays in sync
      // with editableSource. Otherwise preValidateKuikaIds in agent-loop blocks
      // legitimate edits when the LLM references kuikaIds that exist in
      // editableSource but not in displayItems.
      const specialSectionIds = new Set<number>(EditableSourceCodec.SPECIAL_SECTION_TAGS.map(([id]) => id));
      const rootDi = screenDictionary?.[SpecialDIIds.rootDI];
      const rootChildren = Array.isArray(rootDi?.children) ? rootDi.children : undefined;
      const regularRoots: IDisplayItem[] =
        rootChildren && rootChildren.length > 0
          ? rootChildren.filter((c: IDisplayItem) => c && !specialSectionIds.has(c.id))
          : Array.isArray(screenDesign.displayItems)
            ? screenDesign.displayItems
            : [];

      const specialRoots: IDisplayItem[] = [];
      if (screenDictionary) {
        for (const [specialId] of EditableSourceCodec.SPECIAL_SECTION_TAGS) {
          const di = screenDictionary[specialId];
          if (di) specialRoots.push(di);
        }
      }

      allRoots = [...regularRoots, ...specialRoots].filter(Boolean);
      if (allRoots.length > 0) {
        const screenIndex = buildScreenIndex(sd.screenProvider?.state?.screenList);
        context.treeSummary = allRoots.map((di: any) => buildLightTreeNode(di, 0, 10, screenIndex));
      }
      context.editableSource = buildEditableScreenSource(screenDesign, screenDictionary, project);

      const validations: ContextValidationEntry[] = collectScreenValidations(screenDictionary);
      if (validations.length > 0) {
        context.validations = validations;
      }
    }

    const datasources = sd.datasourceProvider?.state?.datasources || sd.state?.datasources;
    if (datasources && datasources.length > 0) {
      // AI Agent / Vision datasources include synthetic placeholder rows added
      // by datasource-provider.tsx so the designer UI tabs can render even
      // when the user has none. Those rows shouldn't surface to the chat LLM,
      // and creating these types is UI-only anyway (see mcp-chat-server
      // prompts/modes.ts).
      context.datasources = datasources
        .filter(
          (ds: any) =>
            ds.datasourceType !== EDatasourceType.AIAgent && ds.datasourceType !== EDatasourceType.VisionAction
        )
        .map((ds: any) => ({
          id: ds.id,
          name: ds.name,
          // IDatasourceDto stores the kind under `datasourceType` (numeric
          // enum). Surface the readable enum name so the chat LLM doesn't
          // render "type: undefined" → "(?)".
          type: typeof ds.datasourceType === "number" ? EDatasourceType[ds.datasourceType] : ds.type
        }));
    }

    const actionDefinitions = sd.projectProvider?.state?.actionDefinitions as IActionDefinitionDto[] | undefined;

    // Datasource dependency graph — KOA-17026. Pivoted view so the LLM can
    // decide when to chain a read action after a mutation on the same
    // screen (e.g. refresh Table after Insert to the same datasource).
    if (
      Array.isArray(actionDefinitions) &&
      actionDefinitions.length > 0 &&
      datasources &&
      datasources.length > 0 &&
      allRoots.length > 0
    ) {
      const deps = deriveDatasourceDependencies(allRoots, actionDefinitions);
      if (deps.length > 0) {
        context.datasourceDependencies = deps;
      }
    }

    const fontNames = project?.componentSetObj?.fontNames;
    if (Array.isArray(fontNames) && fontNames.length > 0) {
      context.fontNames = fontNames;
    }

    const commonActions = ["NAVIGATE", "GoBack", "Notify", "SetValueOf", "SaveRecord", "DeleteRecord"];
    if (Array.isArray(actionDefinitions)) {
      const commonActionIds: Record<string, string> = {};
      for (const actionName of commonActions) {
        const found = (actionDefinitions as NamedActionDefinition[]).find(
          (ad) => ad.name === actionName || ad.actionUniqueName === actionName
        );
        if (found?.id) {
          commonActionIds[actionName] = found.id;
        }
      }
      if (Object.keys(commonActionIds).length > 0) {
        context.commonActionIds = commonActionIds;
      }
    }

    return Object.keys(context).length > 0 ? context : null;
  } catch {
    return null;
  }
}

export function gatherPillContext(): ContextPillData | null {
  try {
    const sd = (window as any).getSdInstance?.();
    if (!sd) return null;

    const screenDesign = sd.screenProvider?.getScreenDesign?.();
    const screenName = screenDesign?.name || sd.screenProvider?.state?.sdName || null;

    let selectedComponent: ContextPillData["selectedComponent"] = null;
    const selectedDi = sd.displayItemProvider?.getSelectedDisplayItem?.();
    if (selectedDi) {
      const kuikaId = selectedDi.id?.toString();
      selectedComponent = {
        kuikaId,
        name: kuikaId === "-1" ? "Screen" : selectedDi.item?.name,
        friendlyName: kuikaId === "-1" ? "Screen" : selectedDi.friendlyName || undefined
      };
    }

    const datasources = sd.datasourceProvider?.state?.datasources || sd.state?.datasources;
    const datasourceCount = Array.isArray(datasources) ? datasources.length : 0;

    return { screenName, selectedComponent, datasourceCount };
  } catch {
    return null;
  }
}
